// Da Vinci Settings Modal (Refactored for Stability)
import { DaVinciState } from "../core/DaVinciState.js";
import { DaVinciEvents } from "../core/DaVinciEvents.js";
import { DaVinciService } from "../DaVinciService.js";

export const DaVinciSettings = {
    // Single source of truth for DOM references
    refs: {},

    render() {
        const overlay = document.createElement('div');
        overlay.className = 'davinci-modal-overlay';
        overlay.id = 'settings-modal-overlay';

        overlay.innerHTML = `
            <div class="davinci-modal">
                <h2><span class="material-symbols-outlined" style="vertical-align:middle">settings</span> ${window.locale.t('davinci.project_settings')}</h2>
                
                <div class="dv-form-group">
                    <label class="dv-form-label">${window.locale.t('davinci.thumbnail')}</label>
                    <div class="dv-thumbnail-preview" id="btn-upload-thumb">
                        <div style="text-align:center" id="thumb-placeholder">
                            <span class="material-symbols-outlined">add_photo_alternate</span>
                            <span style="font-size:0.8rem; margin-top:5px; display:block">${window.locale.t('davinci.cover_upload')}</span>
                        </div>
                    </div>
                </div>

                <div style="display:flex; gap:16px">
                    <div class="dv-form-group" style="flex:1">
                        <label class="dv-form-label">${window.locale.t('davinci.isbn')}</label>
                        <input type="text" class="dv-form-input" id="meta-isbn" placeholder="979-11-..." />
                    </div>
                    <div class="dv-form-group" style="flex:1">
                        <label class="dv-form-label">${window.locale.t('davinci.uci')}</label>
                        <input type="text" class="dv-form-input" id="meta-uci" placeholder="Universal Content ID" />
                    </div>
                </div>
                
                <div style="display:flex; gap:20px; margin-bottom:20px; padding:15px; background:#111; border-radius:8px; border:1px solid #333">
                     <label class="dv-checkbox-group">
                        <input type="checkbox" id="meta-isAdult">
                        <div class="dv-checkbox-box"><span class="material-symbols-outlined text-xs">check</span></div>
                        <span style="font-size:0.9rem; font-weight:bold; color:#f44">${window.locale.t('davinci.is_adult')}</span>
                     </label>
                     <label class="dv-checkbox-group">
                        <input type="checkbox" id="meta-isAI">
                        <div class="dv-checkbox-box"><span class="material-symbols-outlined text-xs">check</span></div>
                        <span style="font-size:0.9rem; font-weight:bold; color:#a8f">${window.locale.t('davinci.is_ai')}</span>
                     </label>
                </div>

                <div class="dv-form-group">
                    <label class="dv-form-label">${window.locale.t('davinci.bgm_url_label')}</label>
                    <div style="display:flex; gap:10px; align-items:center">
                        <input type="text" class="dv-form-input" id="meta-bgm-input" placeholder="https://..." style="font-family:monospace; font-size:0.8rem" />
                        <span class="material-symbols-outlined" style="color:#f44; cursor:pointer; display:none" id="btn-clear-bgm">close</span>
                    </div>
                    <div style="font-size:0.75rem; color:#666; margin-top:5px">${window.locale.t('davinci.suno_tip')}</div>
                </div>
                
                <div class="dv-form-group">
                    <label class="dv-form-label">${window.locale.t('davinci.author_note')}</label>
                    <textarea class="dv-form-input" id="meta-note" rows="3" placeholder="${window.locale.t('editor.note_placeholder')}"></textarea>
                </div>

                <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:24px">
                    <button class="davinci-block-btn" style="background:var(--davinci-accent); color:#000; font-weight:bold" id="btn-close-settings">
                        ${window.locale.t('davinci.save_settings')}
                    </button>
                </div>
            </div>
        `;

        // Cache references
        this.refs = {
            overlay,
            isbn: overlay.querySelector('#meta-isbn'),
            uci: overlay.querySelector('#meta-uci'),
            note: overlay.querySelector('#meta-note'),
            isAdult: overlay.querySelector('#meta-isAdult'),
            isAI: overlay.querySelector('#meta-isAI'),
            bgmInput: overlay.querySelector('#meta-bgm-input'),
            bgmClear: overlay.querySelector('#btn-clear-bgm'),
            thumbBtn: overlay.querySelector('#btn-upload-thumb'),
            thumbPlaceholder: overlay.querySelector('#thumb-placeholder'),
            closeBtn: overlay.querySelector('#btn-close-settings')
        };

        this.bindEvents();

        return overlay;
    },

    bindEvents() {
        if (!this.refs.overlay) return;

        // Inputs (One-way binding: DOM -> State)
        const bindText = (key, el) => {
            el.oninput = (e) => {
                // Determine hierarchy based on key
                // Simplified: DaVinciState logic
                const s = DaVinciState.get();
                s.metadata[key] = e.target.value;
                // Note: Proxy might trigger update, we must ensure update() doesn't clobber focus
            };
        };

        bindText('isbn', this.refs.isbn);
        bindText('uci', this.refs.uci);
        bindText('authorNote', this.refs.note); // Key mismatch fixed: authorNote

        const bindCheck = (key, el) => {
            el.onchange = (e) => {
                const s = DaVinciState.get();
                s.metadata[key] = e.target.checked;
            };
        };
        bindCheck('isAdult', this.refs.isAdult);
        bindCheck('isAI', this.refs.isAI);

        // BGM Logic
        this.refs.bgmInput.onchange = (e) => {
            const val = e.target.value;
            const res = DaVinciService.processBgmUrl(val);
            if (res.error) {
                window.Modal.alert(res.error);
                return;
            }
            const s = DaVinciState.get();
            s.metadata.bgm = res.url;
            if (res.url !== val) this.refs.bgmInput.value = res.url;
            this.refs.bgmClear.style.display = res.url ? 'block' : 'none';
        };

        this.refs.bgmClear.onclick = () => {
            const s = DaVinciState.get();
            s.metadata.bgm = null;
            this.refs.bgmInput.value = '';
            this.refs.bgmClear.style.display = 'none';
        };

        // Thumbnail Upload
        this.refs.thumbBtn.onclick = () => {
            const input = document.createElement('input');
            input.type = 'file'; input.accept = 'image/*';
            input.onchange = async (e) => {
                if (e.target.files[0]) {
                    try {
                        const url = await DaVinciService.convertToWebP(e.target.files[0]);
                        const s = DaVinciState.get();
                        s.metadata.thumbnail = url;
                        // Initial update local
                        this.updateThumbnail(url);
                    } catch (err) { window.Modal.alert(err); }
                }
            };
            input.click();
        };

        // Close
        this.refs.closeBtn.onclick = () => {
            DaVinciState.toggleSettings(false);
        };

        // Listen for External Toggles
        DaVinciEvents.on(DaVinciEvents.ON_STATE_CHANGE, ({ prop, value, state }) => {
            if (prop === 'ui' && value.isSettingsOpen !== undefined) {
                this.toggleVisibility(value.isSettingsOpen);
                if (value.isSettingsOpen) {
                    this.syncFromState(state);
                }
            }
        });
    },

    toggleVisibility(isOpen) {
        if (isOpen) {
            this.refs.overlay.style.display = 'flex';
            requestAnimationFrame(() => this.refs.overlay.classList.add('open'));
        } else {
            this.refs.overlay.classList.remove('open');
            setTimeout(() => {
                if (!this.refs.overlay.classList.contains('open')) {
                    this.refs.overlay.style.display = 'none';
                }
            }, 300);
        }
    },

    syncFromState(state) {
        // Only update if value differs to avoid cursor jump (though we usually sync on open only)
        // If the modal is open and user is typing, we should NOT overwrite strictly unless it's an external change.
        // For simplicity, we assume this is called mainly on Open.
        const m = state.metadata;

        const setVal = (el, val) => { if (el && el.value !== val) el.value = val || ''; };
        setVal(this.refs.isbn, m.isbn);
        setVal(this.refs.uci, m.uci);
        setVal(this.refs.note, m.authorNote);
        setVal(this.refs.bgmInput, m.bgm);

        if (this.refs.isAdult) this.refs.isAdult.checked = !!m.isAdult;
        if (this.refs.isAI) this.refs.isAI.checked = !!m.isAI;

        this.refs.bgmClear.style.display = m.bgm ? 'block' : 'none';
        this.updateThumbnail(m.thumbnail);
    },

    updateThumbnail(url) {
        if (url) {
            this.refs.thumbBtn.style.backgroundImage = `url('${url}')`;
            this.refs.thumbPlaceholder.style.display = 'none';
        } else {
            this.refs.thumbBtn.style.backgroundImage = '';
            this.refs.thumbPlaceholder.style.display = 'block';
        }
    }
};
